import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Home, Package, Camera, ClipboardList, X, Calendar, Trash2, ChevronDown, ChevronRight, MapPin, Zap, LayoutGrid, List, AlertTriangle } from 'lucide-react';
import { Project, ProjectFilter } from '../types';
import { 
  getAllProjects, 
  getProjectsByStatus, 
  getProjectCounts, 
  migrateProjectStatus,
  getActiveProjectsWithCounts,
  getProjectsByFilterWithCounts,
  getDeletedProjects,
  restoreProject,
  permanentlyDeleteProject
} from '../services/projectService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { Timestamp } from 'firebase/firestore';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from '../firebase';
import { BASE_COORDINATES, calculateDistance, formatDistance, geocodeAddress } from '../utils/geocoding';
import CameraComponent from '../components/CameraComponent';
import ConfirmDialog from '../components/ConfirmDialog';
import TemporaryPhotoComponent from '../components/TemporaryPhotoComponent';
import { getTemporaryPhotos, associateTemporaryPhotos } from '../services/photoService';
import { StatusBadge, StatusDot, getStatusLabel as uiGetStatusLabel } from '../components/ui/StatusBadge';
import { MetricPill } from '../components/ui/MetricPill';
import { AssigneeAvatars, getAssignees } from '../components/ui/AssigneeAvatars';
import { EmptyState } from '../components/ui/EmptyState';
import { ActionSheet } from '../components/ui/ActionSheet';
import { FloatingActionButton } from '../components/ui/FloatingActionButton';
import { BottomSheet } from '../components/ui/BottomSheet';
import { SearchBar } from '../components/ui/SearchBar';
import { QuickAddProjectSheet } from '../components/QuickAddProjectSheet';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useRefresh, useFullScreen } from '../components/Layout';
import MobileLanes from '../components/MobileLanes';
import { computeLanes, nextDate as laneNextDate, startOfDay as laneStartOfDay } from '../utils/lanes';

interface ProjectCounts {
  [projectId: string]: {
    roomCount: number;
    itemCount: number;
    photoCount: number;
    checklistCount: number;
    damageReportCount: number;
  };
}

type SortOption = 'evaluationDateAsc' | 'evaluationDateDesc' | 'upcomingDates' | 'none';
type ViewOption = 'card' | 'list' | 'calendar';
type CalendarViewType = 'week' | 'month';

const STORAGE_KEYS = {
  VIEW_OPTION: 'stadsauktion_view_option',
  FILTER: 'stadsauktion_filter',
  SORT_OPTION: 'stadsauktion_sort_option',
  CALENDAR_VIEW_TYPE: 'stadsauktion_calendar_view_type'
} as const;

type FilterType = ProjectFilter;

// Filter labels come from StatusBadge so a pill, the badge on the resulting
// cards, and the empty-state message all say the same word. They used to
// disagree — tapping "Hämtning" returned cards reading "Värderad", and
// "Hämtning" named one filter while "Hämtning planerad" was a different
// filter's result. That collision is what made the row untrustworthy.
// 'active' and 'needs_attention' are not project statuses, so they are named
// here; everything else defers to the single source of truth.
const FILTER_TABS: { value: FilterType; label: string }[] = [
  { value: 'active', label: 'Aktiva' },
  { value: 'bostad', label: uiGetStatusLabel('bostad') },
  { value: 'värderad', label: uiGetStatusLabel('värderad') },
  { value: 'planerad', label: uiGetStatusLabel('planerad') },
  { value: 'väntar', label: uiGetStatusLabel('väntar') },
  { value: 'needs_attention', label: 'Behöver åtgärd' },
];

const MORE_FILTERS: { value: FilterType; label: string }[] = [
  { value: 'avslutad', label: uiGetStatusLabel('avslutad') },
  { value: 'avbruten', label: uiGetStatusLabel('avbruten') },
  { value: 'all', label: 'Alla' },
];

const ProjectListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  const { setIsFullScreen } = useFullScreen();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectCounts, setProjectCounts] = useState<ProjectCounts>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>(() => {
    const urlFilter = new URLSearchParams(location.search).get('filter') as FilterType | null;
    if (urlFilter) return urlFilter;
    const savedFilter = localStorage.getItem(STORAGE_KEYS.FILTER);
    return (savedFilter as FilterType) || 'active';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>(() => {
    const savedSort = localStorage.getItem(STORAGE_KEYS.SORT_OPTION);
    return (savedSort as SortOption) || 'upcomingDates';
  });
  const [viewOption, setViewOption] = useState<ViewOption>(() => {
    const savedView = localStorage.getItem(STORAGE_KEYS.VIEW_OPTION);
    return (savedView as ViewOption) || 'card';
  });
  const [_migrating, setMigrating] = useState(false);
  const isOnline = useOnlineStatus();
  const [projectDistances, setProjectDistances] = useState<{[key: string]: number | null}>({});
  const [calculatingDistances, setCalculatingDistances] = useState<{[key: string]: boolean}>({});
  const [showCamera, setShowCamera] = useState(false);
  const [showTempPhotos, setShowTempPhotos] = useState(false);
  const [tempPhotoCount, setTempPhotoCount] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [savingPhotos, setSavingPhotos] = useState(false);
  const [photoSaveSuccess, setPhotoSaveSuccess] = useState(false);
  const [savedProjectName, setSavedProjectName] = useState('');
  const [geoLoadingStarted, setGeoLoadingStarted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarViewType, setCalendarViewType] = useState<CalendarViewType>(() => {
    const savedCalendarView = localStorage.getItem(STORAGE_KEYS.CALENDAR_VIEW_TYPE);
    return (savedCalendarView as CalendarViewType) || 'week';
  });
  const [showDeletedProjects, setShowDeletedProjects] = useState(false);
  const [deletedProjects, setDeletedProjects] = useState<Array<Project & { deletedAt: any, expiresAt: any }>>([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
    duration: number;
  } | null>(null);
  const [permanentDeleteConfirmId, setPermanentDeleteConfirmId] = useState<string | null>(null);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  // Sync filter from URL search params (sidebar navigation)
  useEffect(() => {
    const urlFilter = new URLSearchParams(location.search).get('filter') as FilterType | null;
    if (urlFilter && urlFilter !== filter) {
      setFilter(urlFilter);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Helper functions (unchanged business logic) ---

  const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  };

  const groupProjectsByDate = (projects: Project[]) => {
    const groups: { [dateKey: string]: Project[] } = {};
    projects.forEach(project => {
      const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
      const evaluationDate = getDateFromTimestamp(project.dates.evaluation);
      const relevantDate = pickupDate || evaluationDate;
      if (relevantDate) {
        const dateKey = relevantDate.toDateString();
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(project);
      }
    });
    return groups;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const start = new Date(firstDay);
    start.setDate(firstDay.getDate() - ((firstDay.getDay() + 6) % 7));
    const end = new Date(lastDay);
    end.setDate(lastDay.getDate() + (7 - ((lastDay.getDay() + 6) % 7)));
    const days: Date[] = [];
    const current = new Date(start);
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const getDaysInWeek = (date: Date): Date[] => {
    const days: Date[] = [];
    const startOfWeek = new Date(date);
    const dayOfWeek = startOfWeek.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(startOfWeek.getDate() - daysSinceMonday);
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  useEffect(() => {
    if (filter !== 'active' && sortOption === 'upcomingDates') {
      setSortOption('evaluationDateDesc');
      localStorage.setItem(STORAGE_KEYS.SORT_OPTION, 'evaluationDateDesc');
    }
  }, [filter, sortOption]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (filter === 'needs_attention') {
        const { projects: allProjects, counts } = await getProjectsByFilterWithCounts('all');
        const filteredProjects = allProjects.filter(project => projectNeedsAttention(project));
        setProjects(filteredProjects);
        const filteredCounts: ProjectCounts = {};
        filteredProjects.forEach(project => {
          if (counts[project.id]) filteredCounts[project.id] = counts[project.id];
        });
        setProjectCounts(filteredCounts);
      } else {
        const { projects: filteredProjects, counts } = await getProjectsByFilterWithCounts(
          filter as 'active' | 'all' | Project['status']
        );
        setProjects(filteredProjects);
        setProjectCounts(counts);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Det gick inte att hämta projekten. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Register pull-to-refresh handler
  useEffect(() => {
    registerRefreshHandler(fetchProjects);
    return () => unregisterRefreshHandler();
  }, [fetchProjects, registerRefreshHandler, unregisterRefreshHandler]);

  /** Get the most relevant date for a project (matches what the card displays) */
  const getRelevantDate = (project: Project): Date | null => {
    const pickupDate = project.dates.scheduledPickup?.toDate() || null;
    const evaluationDate = project.dates.evaluation?.toDate() || null;
    if (['värderad', 'planerad'].includes(project.status) && pickupDate) return pickupDate;
    return evaluationDate || pickupDate;
  };

  const sortProjects = (projects: Project[], sortOption: SortOption): Project[] => {
    return [...projects].sort((a, b) => {
      if (sortOption === 'evaluationDateAsc') {
        const aDate = getRelevantDate(a);
        const bDate = getRelevantDate(b);
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate.getTime() - bDate.getTime();
      } else if (sortOption === 'evaluationDateDesc') {
        const aDate = getRelevantDate(a);
        const bDate = getRelevantDate(b);
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate.getTime() - aDate.getTime();
      } else if (sortOption === 'upcomingDates') {
        const aPickupDate = a.dates.scheduledPickup ? a.dates.scheduledPickup.toDate() : null;
        const bPickupDate = b.dates.scheduledPickup ? b.dates.scheduledPickup.toDate() : null;
        const aEvaluationDate = a.dates.evaluation ? a.dates.evaluation.toDate() : null;
        const bEvaluationDate = b.dates.evaluation ? b.dates.evaluation.toDate() : null;

        const getNextRelevantDate = (project: Project, pickupDate: Date | null, evaluationDate: Date | null): Date | null => {
          if (['värderad', 'planerad'].includes(project.status) && pickupDate) return pickupDate;
          if (['bostad', 'hem'].includes(project.status) && evaluationDate) return evaluationDate;
          if (project.status === 'väntar' && evaluationDate) return evaluationDate;
          return pickupDate || evaluationDate;
        };

        const aNextDate = getNextRelevantDate(a, aPickupDate, aEvaluationDate);
        const bNextDate = getNextRelevantDate(b, bPickupDate, bEvaluationDate);

        if (aNextDate && bNextDate) {
          const dateDiff = aNextDate.getTime() - bNextDate.getTime();
          if (dateDiff === 0) {
            const aTime = (['värderad', 'planerad'].includes(a.status) && a.dates.scheduledPickupTime) ? 
              a.dates.scheduledPickupTime : 
              (['bostad', 'hem'].includes(a.status) && a.dates.evaluationStartTime) ? 
              a.dates.evaluationStartTime : null;
            const bTime = (['värderad', 'planerad'].includes(b.status) && b.dates.scheduledPickupTime) ? 
              b.dates.scheduledPickupTime : 
              (['bostad', 'hem'].includes(b.status) && b.dates.evaluationStartTime) ? 
              b.dates.evaluationStartTime : null;
            if (aTime && bTime) return aTime.localeCompare(bTime);
            const statusPriority: Record<Project['status'], number> = { 
              'värderad': 1, 'planerad': 2, 'bostad': 3, 'hem': 3, 'väntar': 4, 'avslutad': 5, 'avbruten': 6 
            };
            return (statusPriority[a.status] || 999) - (statusPriority[b.status] || 999);
          }
          return dateDiff;
        }
        if (aNextDate) return -1;
        if (bNextDate) return 1;
        const statusPriority: Record<Project['status'], number> = { 
          'värderad': 1, 'planerad': 2, 'bostad': 3, 'hem': 3, 'väntar': 4, 'avslutad': 5, 'avbruten': 6 
        };
        return (statusPriority[a.status] || 999) - (statusPriority[b.status] || 999);
      }
      return 0;
    });
  };

  const getDateFromTimestamp = (timestamp: Timestamp | null | undefined): Date | null => {
    return timestamp?.toDate() || null;
  };

  const isDateInPast = (date: Date | null) => {
    if (!date) return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < now;
  };

  const projectNeedsAttention = (project: Project) => {
    const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
    const evaluationDate = getDateFromTimestamp(project.dates.evaluation);
    if (pickupDate) return false;
    const isEvaluationInPast = isDateInPast(evaluationDate);
    return isEvaluationInPast && !['avslutad', 'avbruten'].includes(project.status);
  };

  const filteredResult = useMemo(() => {
    let filtered = projects;
    switch (filter) {
      case 'active':
        filtered = projects.filter(project => !['avslutad', 'avbruten'].includes(project.status));
        break;
      case 'needs_attention':
        filtered = projects.filter(project => projectNeedsAttention(project));
        break;
      case 'all':
        filtered = projects;
        break;
      default:
        filtered = projects.filter(project => project.status === filter);
    }
    filtered = filtered.filter(project => {
      const searchLower = searchTerm.toLowerCase();
      return (
        project.address.street.toLowerCase().includes(searchLower) ||
        project.address.city.toLowerCase().includes(searchLower) ||
        project.contact.name.toLowerCase().includes(searchLower) ||
        project.evaluator.toLowerCase().includes(searchLower)
      );
    });
    // Same order as the desktop table: upcoming soonest first, then passed
    // dates most recent first, then undated by status. Nothing is hidden.
    const today = laneStartOfDay(new Date());
    const rank: Record<Project['status'], number> = { 'värderad': 1, 'planerad': 2, 'bostad': 3, 'hem': 3, 'väntar': 4, 'avslutad': 5, 'avbruten': 6 };
    const group = (p: Project) => { const d = laneNextDate(p).date; return !d ? 2 : d >= today ? 0 : 1; };
    const ordered = [...filtered].sort((a, b) => {
      const ga = group(a), gb = group(b);
      if (ga !== gb) return ga - gb;
      const da = laneNextDate(a).date, db = laneNextDate(b).date;
      if (!da || !db) return (rank[a.status] || 9) - (rank[b.status] || 9);
      const diff = ga === 0 ? da.getTime() - db.getTime() : db.getTime() - da.getTime();
      if (diff !== 0) return diff;
      return (laneNextDate(a).time || '').localeCompare(laneNextDate(b).time || '');
    });
    return { list: ordered, hidden: 0 };
  }, [projects, filter, searchTerm]);

  const filteredAndSortedProjects = filteredResult.list;
  const hiddenByUpcoming = filteredResult.hidden;

  useEffect(() => {
    if (loading || projects.length === 0) return;
    setGeoLoadingStarted(true);
  }, [loading, projects.length]);
  
  const calculateProjectDistance = useCallback(async (project: Project) => {
    const projectId = project.id;
    if (calculatingDistances[projectId] || projectDistances[projectId] !== undefined) return;
    setCalculatingDistances(prev => ({ ...prev, [projectId]: true }));
    try {
      const coords = await geocodeAddress(project.address.street, project.address.city);
      if (coords) {
        setProjectDistances(prev => ({ ...prev, [projectId]: calculateDistance(BASE_COORDINATES, coords) }));
      } else {
        setProjectDistances(prev => ({ ...prev, [projectId]: null }));
      }
    } catch (error) {
      console.error('Error calculating distance for project:', projectId, error);
      setProjectDistances(prev => ({ ...prev, [projectId]: null }));
    } finally {
      setCalculatingDistances(prev => ({ ...prev, [projectId]: false }));
    }
  }, []);

  useEffect(() => {
    if (!geoLoadingStarted || loading || projects.length === 0) return;
    const scheduleGeoCalculation = (project: Project, index: number) => {
      if (projectDistances[project.id] !== undefined || calculatingDistances[project.id]) return;
      const delay = index < 5 ? index * 300 : 1500 + (index - 5) * 800;
      if (typeof window.requestIdleCallback !== 'undefined') {
        window.requestIdleCallback(() => calculateProjectDistance(project), { timeout: 10000 });
      } else {
        setTimeout(() => calculateProjectDistance(project), delay);
      }
    };
    filteredAndSortedProjects.forEach((project: Project, index: number) => {
      scheduleGeoCalculation(project, index);
    });
  }, [geoLoadingStarted, projects, loading, calculateProjectDistance, projectDistances, calculatingDistances, filteredAndSortedProjects]);

  const handleMigrateStatus = async () => {
    try {
      setMigrating(true);
      await migrateProjectStatus();
      const fetchedProjects = await getAllProjects();
      setProjects(fetchedProjects);
    } catch (err) {
      console.error('Error migrating project status:', err);
    } finally {
      setMigrating(false);
    }
  };

  const formatDateShort = (date: Date | null): string => {
    if (!date) return '';
    const days = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
    return `${days[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`;
  };

  const formatDateWithDay = (date: Date | null): string => {
    if (!date) return 'Inget datum';
    const days = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
    return `${days[date.getDay()]} ${date.toLocaleDateString('sv-SE')}`;
  };

  const formatWeekRange = (date: Date): string => {
    const weekDays = getDaysInWeek(date);
    const startDate = weekDays[0];
    const endDate = weekDays[6];
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDate.getDate()}-${endDate.getDate()} ${endDate.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}`;
    } else {
      return `${startDate.getDate()} ${startDate.toLocaleDateString('sv-SE', { month: 'short' })} - ${endDate.getDate()} ${endDate.toLocaleDateString('sv-SE', { month: 'short', year: 'numeric' })}`;
    }
  };

  // Filter-only values are named here; real project statuses delegate to
  // StatusBadge so the wording stays identical everywhere it appears.
  const getStatusLabel = (status: FilterType) => {
    switch (status) {
      case 'active': return 'Aktiva';
      case 'needs_attention': return 'Behöver åtgärd';
      case 'all': return 'Alla';
      default: return uiGetStatusLabel(status);
    }
  };

  useEffect(() => {
    const tempPhotos = getTemporaryPhotos();
    setTempPhotoCount(tempPhotos.length);
  }, [showCamera, showTempPhotos]);

  // Fires per saved photo while the camera stays open for the next shot.
  const handlePhotoTaken = (photoId: string) => {
    setTempPhotoCount(getTemporaryPhotos().length);
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setTempPhotoCount(getTemporaryPhotos().length);
  };

  const handleSaveToProject = async (projectId: string) => {
    try {
      setSavingPhotos(true);
      setSelectedProjectId(projectId);
      await associateTemporaryPhotos(projectId);
      const project = projects.find(p => p.id === projectId);
      if (project) setSavedProjectName(project.address.street);
      setTempPhotoCount(0);
      setShowProjectSelector(false);
      setPhotoSaveSuccess(true);
      setTimeout(() => { setPhotoSaveSuccess(false); setSavedProjectName(''); }, 3000);
    } catch (error) {
      console.error('Error saving photos to project:', error);
    } finally {
      setSavingPhotos(false);
      setSelectedProjectId(null);
    }
  };

  useEffect(() => { localStorage.setItem(STORAGE_KEYS.VIEW_OPTION, viewOption); }, [viewOption]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.FILTER, filter); }, [filter]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.SORT_OPTION, sortOption); }, [sortOption]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.CALENDAR_VIEW_TYPE, calendarViewType); }, [calendarViewType]);

  useEffect(() => {
    if (showDeletedProjects) {
      const fetchDeletedProjects = async () => {
        try {
          setLoadingDeleted(true);
          const deleted = await getDeletedProjects();
          setDeletedProjects(deleted);
        } catch (err) {
          console.error('Error fetching deleted projects:', err);
        } finally {
          setLoadingDeleted(false);
        }
      };
      fetchDeletedProjects();
    }
  }, [showDeletedProjects]);
  
  const handleRestoreProject = async (projectId: string) => {
    if (!isOnline) return;
    try {
      setRestoring(projectId);
      await restoreProject(projectId);
      setDeletedProjects(prev => prev.filter(p => p.id !== projectId));
      const fetchedProjects = await getAllProjects();
      setProjects(fetchedProjects);
      setNotification({ type: 'success', message: 'Projektet har återställts', duration: 5000 });
    } catch (err) {
      console.error('Error restoring project:', err);
      setNotification({ type: 'error', message: 'Kunde inte återställa projektet', duration: 5000 });
    } finally {
      setRestoring(null);
    }
  };
  
  const handlePermanentDelete = (projectId: string) => {
    if (!isOnline) return;
    setPermanentDeleteConfirmId(projectId);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeleteConfirmId) return;
    try {
      await permanentlyDeleteProject(permanentDeleteConfirmId);
      setDeletedProjects(prev => prev.filter(p => p.id !== permanentDeleteConfirmId));
      setNotification({ type: 'success', message: 'Projektet har raderats permanent', duration: 5000 });
      setPermanentDeleteConfirmId(null);
    } catch (err) {
      console.error('Error permanently deleting project:', err);
      setNotification({ type: 'error', message: 'Kunde inte radera projektet permanent', duration: 5000 });
      setPermanentDeleteConfirmId(null);
    }
  };

  const getNextDate = (project: Project) => {
    const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
    const evaluationDate = getDateFromTimestamp(project.dates.evaluation);
    if (['värderad', 'planerad'].includes(project.status) && pickupDate) {
      return { date: pickupDate, time: project.dates.scheduledPickupTime, type: 'pickup' as const };
    }
    return { date: evaluationDate, time: project.dates.evaluationStartTime, type: 'evaluation' as const };
  };

  // --- Render helpers ---

  const renderProjectCard = (project: Project) => {
    const { date, time, type } = getNextDate(project);
    const distance = projectDistances[project.id];
    const counts = projectCounts[project.id];
    const needsAttention = projectNeedsAttention(project);

    return (
      <Link
        key={project.id}
        to={`/projects/${project.id}`}
        className={`block bg-[var(--bg-surface)] rounded-xl border press-row active:scale-[0.98] ${
          needsAttention ? 'border-amber-400/60 dark:border-amber-400/50 bg-amber-50/30 dark:bg-amber-500/10' : 'border-[var(--border-default)] hover:border-[var(--border-default)]'
        }`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <StatusDot status={project.status} />
                <h2 className="text-[17px] leading-[22px] tracking-[-0.01em] font-semibold text-[var(--text-primary)] truncate">
                  {project.address.street}
                </h2>
              </div>
              <div className="flex items-center text-[13px] leading-[18px] text-[var(--text-tertiary)] ml-[18px]">
                <span>{project.address.city}</span>
                {distance !== null && distance !== undefined && (
                  <>
                    <span className="mx-1.5 text-[var(--text-tertiary)]">&middot;</span>
                    <span>{formatDistance(distance)}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {date ? (
                <div className="text-right">
                  <div className="text-[15px] leading-[20px] font-medium text-[var(--text-primary)] tabular-nums">{formatDateShort(date)}</div>
                  {time && <div className="text-[13px] leading-[18px] text-[var(--text-tertiary)] tabular-nums">{time}</div>}
                </div>
              ) : (
                <span className="text-[13px] leading-[18px] text-[var(--text-tertiary)]">Inget datum</span>
              )}
            </div>
          </div>

          {needsAttention && (
            <div className="flex items-center gap-1.5 mt-2 ml-[18px] text-amber-600 dark:text-amber-400">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <span className="text-sm font-semibold">Behöver åtgärd</span>
            </div>
          )}
          {project.needsReview && (
            // Imported from the valuator's calendar entry: the address/contact
            // were parsed from the event title, so a person should confirm them.
            <div className="flex items-center gap-1.5 mt-2 ml-[18px] text-blue-600 dark:text-blue-400">
              <Calendar size={16} className="flex-shrink-0" />
              <span className="text-sm font-semibold">Från kalendern – kontrollera uppgifter</span>
            </div>
          )}

          <div className="flex items-center justify-between mt-3 ml-[18px]">
            <div className="flex items-center gap-2 min-w-0">
              <StatusBadge status={project.status} size="sm" />
              {/* Whose job is this right now — valuator before pickup is
                  scheduled, the pickup team after. */}
              <AssigneeAvatars names={getAssignees(project)} />
            </div>
            <div className="flex items-center gap-1.5">
              {counts && (
                <>
                  <MetricPill icon={Home} count={counts.roomCount} />
                  <MetricPill icon={Package} count={counts.itemCount} />
                  <MetricPill icon={Camera} count={counts.photoCount} />
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const renderListRow = (project: Project) => {
    const { date, time, type } = getNextDate(project);
    const needsAttention = projectNeedsAttention(project);

    return (
      <tr
        key={project.id}
        className={`press-row cursor-pointer ${needsAttention ? 'bg-amber-50/50 dark:bg-amber-500/10 border-l-2 border-l-amber-500' : ''}`}
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        <td className="py-3.5 pl-3 pr-2">
          <div className="flex items-center gap-2.5">
            <StatusDot status={project.status} />
            <div className="min-w-0">
              <div className="text-base font-medium text-[var(--text-primary)] truncate max-w-[180px] lg:max-w-[300px]">
                {project.address.street}
              </div>
              <div className="text-sm text-[var(--text-secondary)] truncate max-w-[180px] lg:max-w-[300px]">
                {project.address.city}
              </div>
            </div>
            {needsAttention && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
          </div>
        </td>
        <td className="px-2 py-3.5 hidden xs:table-cell">
          <StatusBadge status={project.status} size="sm" />
        </td>
        <td className="px-2 py-3.5">
          {date ? (
            <div>
              <div className="text-sm font-medium text-[var(--text-secondary)] tabular-nums">{formatDateShort(date)}</div>
              {time && <div className="text-xs text-[var(--text-tertiary)] tabular-nums">{time}</div>}
            </div>
          ) : (
            <span className="text-sm text-[var(--text-tertiary)]">-</span>
          )}
        </td>
        <td className="px-2 py-3.5 hidden lg:table-cell">
          {/* Same stage-dependent rule as the card, so the two views never
              disagree about who owns a project. */}
          <AssigneeAvatars names={getAssignees(project)} />
        </td>
        <td className="pr-3 py-3.5 text-right">
          <ChevronRight size={16} className="text-[var(--text-tertiary)] inline" />
        </td>
      </tr>
    );
  };

  const renderCalendarView = () => {
    const days = calendarViewType === 'week' ? getDaysInWeek(currentMonth) : getDaysInMonth(currentMonth);
    
    return (
      <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
          <button
            onClick={() => {
              const prev = new Date(currentMonth);
              if (calendarViewType === 'week') prev.setDate(prev.getDate() - 7);
              else prev.setMonth(prev.getMonth() - 1);
              setCurrentMonth(prev);
            }}
            aria-label={calendarViewType === 'week' ? 'Föregående vecka' : 'Föregående månad'}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]"
          >
            <ChevronDown size={16} className="rotate-90" />
          </button>
          <span className="text-sm font-semibold text-[var(--text-primary)]">
            {calendarViewType === 'week'
              ? formatWeekRange(currentMonth)
              : currentMonth.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => {
              const next = new Date(currentMonth);
              if (calendarViewType === 'week') next.setDate(next.getDate() + 7);
              else next.setMonth(next.getMonth() + 1);
              setCurrentMonth(next);
            }}
            aria-label={calendarViewType === 'week' ? 'Nästa vecka' : 'Nästa månad'}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]"
          >
            <ChevronDown size={16} className="-rotate-90" />
          </button>
        </div>
        <div className="flex items-center justify-center gap-1 py-2 border-b border-[var(--border-subtle)]">
          <button
            onClick={() => { setCalendarViewType('week'); localStorage.setItem(STORAGE_KEYS.CALENDAR_VIEW_TYPE, 'week'); }}
            className={`px-4 min-h-[44px] rounded-full text-xs font-medium transition-colors ${calendarViewType === 'week' ? 'bg-accent-600 text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}
          >Vecka</button>
          <button
            onClick={() => { setCalendarViewType('month'); localStorage.setItem(STORAGE_KEYS.CALENDAR_VIEW_TYPE, 'month'); }}
            className={`px-4 min-h-[44px] rounded-full text-xs font-medium transition-colors ${calendarViewType === 'month' ? 'bg-accent-600 text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}
          >Månad</button>
        </div>
        {/* Agenda list — phones. A 7-column grid at 390px gives ~51px cells
            (~39px usable), so project chips truncated street names to 3-4
            characters and fell far below the 44px touch minimum. Only days that
            actually have projects are listed, so the scheduling view is
            readable and tappable on the device this app actually runs on.
            The grid below is preserved unchanged for lg: desktop. */}
        <div className="lg:hidden divide-y divide-[var(--border-subtle)]">
          {(() => {
            const daysWithProjects = days
              .map(date => ({
                date,
                projects: filteredAndSortedProjects.filter(project => {
                  const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
                  const evaluationDate = getDateFromTimestamp(project.dates.evaluation);
                  return (pickupDate && isSameDay(pickupDate, date)) || (evaluationDate && isSameDay(evaluationDate, date));
                }),
              }))
              .filter(d => d.projects.length > 0);

            if (daysWithProjects.length === 0) {
              return (
                <div className="py-10 px-4 text-center text-[15px] text-[var(--text-secondary)]">
                  Inga planerade datum {calendarViewType === 'week' ? 'den här veckan' : 'den här månaden'}.
                </div>
              );
            }

            return daysWithProjects.map(({ date, projects: dayProjects }) => {
              const isToday = isSameDay(date, new Date());
              return (
                <div key={date.toISOString()} className="py-2">
                  <div className="flex items-baseline gap-2 px-4 py-1.5">
                    <span className={`text-[15px] font-semibold ${isToday ? 'text-accent-600 dark:text-accent-400' : 'text-[var(--text-primary)]'}`}>
                      {date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    {isToday && (
                      <span className="text-[12px] font-medium text-accent-600 dark:text-accent-400">Idag</span>
                    )}
                  </div>
                  {dayProjects.map(project => {
                    const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
                    const isPickup = pickupDate && isSameDay(pickupDate, date);
                    const time = isPickup ? project.dates.scheduledPickupTime : project.dates.evaluationStartTime;
                    return (
                      <Link
                        key={`${project.id}-${isPickup ? 'p' : 'e'}`}
                        to={`/projects/${project.id}`}
                        className="press-row flex items-center gap-3 px-4 min-h-[44px] py-2"
                      >
                        <span
                          aria-hidden="true"
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isPickup ? 'bg-emerald-500' : 'bg-blue-500'}`}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] text-[var(--text-primary)] truncate">
                            {project.address.street}
                          </span>
                          <span className="block text-[12px] text-[var(--text-secondary)]">
                            {isPickup ? 'Hämtning' : 'Hembesök'}
                            {time ? ` · ${time}` : ''}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>

        <div className="hidden lg:grid grid-cols-7 text-center text-[12px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider border-b border-[var(--border-subtle)]">
          {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(d => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="hidden lg:grid grid-cols-7 text-sm">
          {days.map((date) => {
            const isToday = isSameDay(date, new Date());
            const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
            const dayProjects = filteredAndSortedProjects.filter(project => {
              const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
              const evaluationDate = getDateFromTimestamp(project.dates.evaluation);
              return (pickupDate && isSameDay(pickupDate, date)) || (evaluationDate && isSameDay(evaluationDate, date));
            });

            return (
              <div
                key={date.toISOString()}
                className={`${calendarViewType === 'week' ? 'min-h-[140px]' : 'min-h-[100px]'} p-1.5 border-b border-r border-[var(--border-subtle)] ${
                  calendarViewType === 'month' && !isCurrentMonth ? 'bg-[var(--bg-primary)]' : ''
                }`}
              >
                <div className={`flex items-center justify-center h-6 w-6 mx-auto mb-1 rounded-full text-xs ${
                  isToday ? 'bg-accent-600 text-white font-semibold' : isCurrentMonth || calendarViewType === 'week' ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'
                }`}>
                  {date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayProjects.map(project => {
                    const pickupDate = getDateFromTimestamp(project.dates.scheduledPickup);
                    const isPickup = pickupDate && isSameDay(pickupDate, date);
                    return (
                      <Link
                        key={`${project.id}-${isPickup ? 'p' : 'e'}`}
                        to={`/projects/${project.id}`}
                        className={`block text-[12px] px-1.5 py-1 rounded-md truncate font-medium ${
                          isPickup
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}
                      >
                        {project.address.street}
                        {isPickup && project.dates.scheduledPickupTime ? ` ${project.dates.scheduledPickupTime}` : ''}
                        {!isPickup && project.dates.evaluationStartTime ? ` ${project.dates.evaluationStartTime}` : ''}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Main render ---
  return (
    <div className="px-4 py-5 lg:px-6 max-w-5xl mx-auto">
      <ConfirmDialog
        isOpen={permanentDeleteConfirmId !== null}
        title="Ta bort"
        message="Är du säker på att du vill radera projektet permanent? Detta kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmPermanentDelete}
        onCancel={() => setPermanentDeleteConfirmId(null)}
      />

      {/* Idag / Denna vecka first: what is on today and this week is the first
          thing the field team sees. Same rule and colours as the desktop. */}
      {!loading && !error && filter === 'active' && !searchTerm && <MobileLanes projects={projects} />}

      {/* Search */}
      <div className="mb-4">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Sök adress, kontakt eller värderare..."
        />
      </div>

      {/* Filter + View controls. The status chip row is gone: one select
          carries every filter, and the list order is fixed (same as desktop). */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
            aria-label="Visa"
            className="appearance-none bg-[var(--bg-surface)] border border-[var(--border-default)] pl-3 pr-8 min-h-[44px] text-sm rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500"
          >
            {[...FILTER_TABS, ...MORE_FILTERS].map(f => <option key={f.value} value={f.value}>{f.value === 'active' ? 'Aktiva' : f.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" />
        </div>
        <div className="flex items-center gap-0.5 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-lg p-1">
          {[
            { value: 'card' as ViewOption, icon: LayoutGrid, label: 'Kortvy' },
            { value: 'list' as ViewOption, icon: List, label: 'Listvy' },
            { value: 'calendar' as ViewOption, icon: Calendar, label: 'Kalendervy' },
          ].map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              onClick={() => setViewOption(value)}
              aria-label={label}
              aria-pressed={viewOption === value}
              className={`p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md transition-colors ${
                viewOption === value ? 'bg-accent-100 dark:bg-accent-900/40 text-accent-600 dark:text-accent-400' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
              }`}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : error ? (
        <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
          <p>{error}</p>
          {!isOnline && <p className="mt-1 text-xs text-red-500 dark:text-red-400">Du är offline.</p>}
        </div>
      ) : filteredAndSortedProjects.length === 0 ? (
        // Distinguish first use from "no results here". Previously all three
        // cases showed the same text, so a brand-new user learned nothing about
        // what the app is for, and a filtered-empty list looked like a bug.
        (() => {
          const isFirstRun = projects.length === 0 && !searchTerm;
          return (
            <EmptyState
              icon={Home}
              title={
                isFirstRun ? 'Börja med en bostad'
                : searchTerm ? 'Inga träffar'
                : 'Inga bostäder här'
              }
              description={
                isFirstRun
                  ? 'Lägg upp bostaden du ska värdera. Den mognar sedan vidare: värderad, hämtning planerad, avslutad.'
                  : searchTerm
                    ? `Ingen bostad matchar "${searchTerm}". Prova en annan adress eller kontakt.`
                    : `Inga bostäder har status "${getStatusLabel(filter)}" just nu.`
              }
              action={
                isFirstRun || !searchTerm ? (
                  <button
                    type="button"
                    onClick={() => setShowQuickAdd(true)}
                    className="inline-flex items-center gap-1.5 px-4 min-h-[44px] bg-accent-600 text-white text-[15px] font-medium rounded-xl hover:bg-accent-700 transition-colors"
                  >
                    <Plus size={16} />
                    Ny bostad
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="inline-flex items-center gap-1.5 px-4 min-h-[44px] bg-[var(--bg-surface-alt)] text-[var(--text-primary)] text-[15px] font-medium rounded-xl transition-colors"
                  >
                    Rensa sökning
                  </button>
                )
              }
            />
          );
        })()
      ) : (
        <>
          {/* Card View — grouped by day.
              Day headers are now unconditional. They used to render only when a
              day had more than one project, so the list's structure appeared and
              vanished as you scrolled and the grouping read as arbitrary. A
              stable header per day is what turns a flat list into a schedule. */}
          {viewOption === 'card' && (() => {
            // The lanes above own today and this week; the list starts after them.
            const lanesShown = filter === 'active' && !searchTerm;
            const lanes = lanesShown ? computeLanes(projects) : null;
            const laneIds = new Set(lanes ? [...lanes.today, ...lanes.week].map(p => p.id) : []);
            const listProjects = filteredAndSortedProjects.filter(p => !laneIds.has(p.id));
            return (
            <div className="space-y-5">
              {(() => {
                const today = laneStartOfDay(new Date());
                const upcoming = listProjects.filter(p => { const d = laneNextDate(p).date; return !!d && d >= today; });
                const passed = listProjects.filter(p => { const d = laneNextDate(p).date; return !!d && d < today; });
                const undated = listProjects.filter(p => !laneNextDate(p).date);
                const renderGroups = (list: Project[]) => {
                  const grouped = groupProjectsByDate(list);
                  const keys = Object.keys(grouped).sort((a, b) => (list === passed ? -1 : 1) * (new Date(a).getTime() - new Date(b).getTime()));
                  return keys.map(dateKey => {
                    const groupProjects = grouped[dateKey];
                    const groupDate = new Date(dateKey);
                    const isToday = groupDate.toDateString() === new Date().toDateString();
                    return (
                      <div key={dateKey}>
                        <h3 className="text-[12px] leading-[16px] font-semibold uppercase tracking-[0.04em] px-1 mb-1.5 flex items-baseline gap-2">
                          <span className={isToday ? 'text-accent-600 dark:text-accent-400' : 'text-[var(--text-secondary)]'}>
                            {isToday ? 'Idag' : groupDate.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </span>
                          <span className="font-normal normal-case tracking-normal tabular-nums text-[var(--text-secondary)]">{groupProjects.length}</span>
                        </h3>
                        <div className="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
                          {groupProjects.map(project => renderProjectCard(project))}
                        </div>
                      </div>
                    );
                  });
                };
                return (
                  <>
                    {renderGroups(upcoming)}
                    {passed.length > 0 && (
                      <div className="pt-2">
                        <h3 className="text-[12px] leading-[16px] font-semibold uppercase tracking-[0.04em] px-1 mb-3 text-[var(--text-secondary)]">Passerade datum · {passed.length}</h3>
                        <div className="space-y-5">{renderGroups(passed)}</div>
                      </div>
                    )}
                    {undated.length > 0 && (
                      <div className="pt-2">
                        <h3 className="text-[12px] leading-[16px] font-semibold uppercase tracking-[0.04em] px-1 mb-1.5 text-[var(--text-secondary)]">Utan datum · {undated.length}</h3>
                        <div className="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">{undated.map(project => renderProjectCard(project))}</div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            );
          })()}

          {/* List View */}
          {viewOption === 'list' && (
            <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border-subtle)]">
                    <th className="py-2.5 pl-3 pr-2 text-left text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Adress</th>
                    <th className="px-2 py-2.5 text-left text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider hidden xs:table-cell">Status</th>
                    <th className="px-2 py-2.5 text-left text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Datum</th>
                    <th className="px-2 py-2.5 text-left text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider hidden lg:table-cell">Team</th>
                    <th className="pr-3 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {filteredAndSortedProjects.map(project => renderListRow(project))}
                </tbody>
              </table>
            </div>
          )}

          {/* Calendar View */}
          {viewOption === 'calendar' && renderCalendarView()}
        </>
      )}

      {/* Trash Bin */}
      <div className="mt-6">
        <button
          className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors py-2"
          onClick={() => setShowDeletedProjects(!showDeletedProjects)}
        >
          <Trash2 size={16} />
          <span>Papperskorg</span>
          {deletedProjects.length > 0 && (
            <span className="bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] px-1.5 rounded-full text-xs font-medium">{deletedProjects.length}</span>
          )}
          <ChevronDown size={14} className={`transition-transform ${showDeletedProjects ? 'rotate-180' : ''}`} />
        </button>
        {showDeletedProjects && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)] p-4 mt-1">
            {loadingDeleted ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-600 border-t-transparent"></div>
              </div>
            ) : deletedProjects.length === 0 ? (
              <p className="text-[var(--text-tertiary)] text-center text-sm py-4">Inga borttagna projekt</p>
            ) : (
              <div className="space-y-2">
                {deletedProjects.map(project => {
                  const deletedDate = project.deletedAt?.toDate ? project.deletedAt.toDate() : new Date();
                  const expiryDate = project.expiresAt?.toDate ? project.expiresAt.toDate() : new Date();
                  return (
                    <div key={project.id} className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
                      <div>
                        <p className="text-base font-medium text-[var(--text-primary)]">{project.address.street}</p>
                        <p className="text-xs text-[var(--text-secondary)]">Borttaget {deletedDate.toLocaleDateString('sv-SE')} &middot; Försvinner {expiryDate.toLocaleDateString('sv-SE')}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        <button
                          onClick={() => handleRestoreProject(project.id)}
                          disabled={restoring === project.id}
                          className="text-xs font-medium text-accent-600 hover:text-accent-700 disabled:text-[var(--text-tertiary)]"
                        >
                          {restoring === project.id ? 'Återställer...' : 'Återställ'}
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(project.id)}
                          className="text-xs font-medium text-red-600 hover:text-red-700"
                        >
                          Radera
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-[var(--text-tertiary)] pt-2">Projekt i papperskorgen sparas i 7 dagar.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Analysis promo — hidden for now */}

      {/* FAB (single combined button with menu) */}
      {photoSaveSuccess && (
        <div className="fixed right-4 z-20 lg:bottom-[5.5rem] animate-slide-up" style={{ bottom: 'calc(49px + env(safe-area-inset-bottom, 0px) + 80px)' }}>
          <div className="bg-emerald-100 text-emerald-800 px-3 py-2 rounded-xl shadow-sm text-xs font-medium">
            Foton sparade till {savedProjectName}
          </div>
        </div>
      )}

      <FloatingActionButton
        icon={<Plus size={24} className="text-white" />}
        onClick={() => setShowFabMenu(true)}
        badge={tempPhotoCount}
        aria-label="Åtgärder"
      />
      
      {showCamera && (
        <CameraComponent
          projectId="temp"
          onPhotoTaken={handlePhotoTaken}
          onClose={handleCameraClose}
        />
      )}
      
      {showTempPhotos && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-surface)] rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <TemporaryPhotoComponent onClose={() => setShowTempPhotos(false)} />
          </div>
        </div>
      )}

      {/* More Filters Action Sheet */}
      <ActionSheet
        isOpen={showMoreFilters}
        onClose={() => setShowMoreFilters(false)}
        actions={MORE_FILTERS.map(f => ({
          label: f.label,
          onClick: () => setFilter(f.value),
        }))}
      />

      {/* FAB Action Sheet */}
      <ActionSheet
        isOpen={showFabMenu}
        onClose={() => setShowFabMenu(false)}
        actions={[
          {
            icon: <Plus size={20} className="text-accent-600" />,
            label: 'Ny bostad',
            onClick: () => setShowQuickAdd(true),
          },
          {
            icon: <ClipboardList size={20} className="text-[var(--text-secondary)]" />,
            label: 'Ny bostad – alla uppgifter',
            onClick: () => navigate('/projects/new'),
          },
          {
            icon: <Camera size={20} className="text-accent-600" />,
            label: 'Ta foto',
            onClick: () => setShowCamera(true),
          },
          ...(tempPhotoCount > 0 ? [
            {
              icon: <Package size={20} className="text-amber-500" />,
              label: `Visa foton (${tempPhotoCount})`,
              onClick: () => setShowTempPhotos(true),
            },
            {
              icon: <ClipboardList size={20} className="text-emerald-500" />,
              label: 'Spara foton till projekt',
              onClick: () => setShowProjectSelector(true),
            },
          ] : []),
        ]}
      />

      <QuickAddProjectSheet
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onCreated={(id) => { fetchProjects(); navigate(`/projects/${id}`); }}
      />

      {/* Project Selector Bottom Sheet */}
      <BottomSheet
        isOpen={showProjectSelector}
        onClose={() => setShowProjectSelector(false)}
        variant="sheet"
        title="Välj projekt"
      >
        <div className="px-3 pb-3 sm:px-4 sm:pb-4">
          <div className="bg-[var(--bg-surface-alt)] rounded-xl overflow-hidden max-h-72 overflow-y-auto">
            {projects.map((project, i) => (
              <React.Fragment key={project.id}>
                {i > 0 && <div className="h-px bg-[var(--border-subtle)] mx-4" />}
                <button
                  onClick={() => handleSaveToProject(project.id)}
                  disabled={savingPhotos}
                  className="w-full flex items-center justify-between px-4 min-h-[52px] active:bg-[var(--bg-surface-hover)] transition-colors disabled:opacity-40"
                >
                  <span className="text-[15px] text-[var(--text-primary)] truncate">{project.address.street}</span>
                  {selectedProjectId === project.id && savingPhotos && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-accent-600 border-t-transparent flex-shrink-0 ml-3"></div>
                  )}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button
            onClick={() => setShowProjectSelector(false)}
            className="w-full mt-2 bg-[var(--bg-surface-alt)] rounded-xl min-h-[52px] text-[15px] font-semibold text-accent-600 dark:text-accent-400 active:bg-[var(--bg-surface-hover)] transition-colors"
          >
            Avbryt
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

export default ProjectListPage;
